import { Despatch, DespatchShipmentContainer, DespatchStatusOption } from "../models/despatch";
import BaseService from "./_base.service";
import API from "../api";
import API_BLOB from "../api/indexBlob";
import { AxiosResponse } from "axios";
import { GetAndCountAll } from "./_base";
import {
  DespatchCreateTaskComment,
  DespatchCrew,
  DespatchCrewCreate,
  DespatchCrewHide,
  DespatchCrewPersist,
  DespatchCrewRemove,
  DespatchDeleteTaskComment,
} from "Models/despatch.crew";
import { DespatchTask, DespatchTaskPersist } from "Models/despatch.task";
import { Team } from "Models/team";
import { TaskComment } from "Models/task.comment";
import { PlaceholderTask, Task } from "Models/task";

const endpoint = "despatches";

const DespatchService = {
  ...BaseService<Despatch>({ endpoint }),
  async getAvailableProducts(
    options?: any
  ): Promise<AxiosResponse<GetAndCountAll<DespatchShipmentContainer>>> {
    return await API.get(`${endpoint}/container/products`, { params: options });
  },
  async update(id: number, data: any): Promise<AxiosResponse<any>> {
    return await API.put(`/${endpoint}/${id}`, data);
  },
  async create(data: any): Promise<AxiosResponse<any>> {
    return await API.post(`/${endpoint}`, data);
  },
  async printDeliveryDocket(id: string | number): Promise<AxiosResponse> {
    return await API_BLOB.post(`/${endpoint}/reports/generate`, { despatchId: id });
  },
  async getStatuses(): Promise<AxiosResponse<DespatchStatusOption[]>> {
    return await API.get(`/${endpoint}/statuses`);
  },
  async getTasksByTeam(params: {
    companyId: number | number[];
    startDate: string;
    endDate: string;
  }): Promise<AxiosResponse<DespatchCrew[]>> {
    return await API.get(`/${endpoint}/tasks/team`, { params });
  },
  async getTasks(params: {
    companyId: number | number[];
    startDate?: string | null;
    endDate?: string | null;
  }): Promise<AxiosResponse<DespatchTask[]>> {
    return await API.get(`/${endpoint}/tasks`, { params });
  },
  async persistTaskData(data: DespatchTaskPersist): Promise<AxiosResponse<boolean>> {
    return await API.put(`/${endpoint}/tasks/save/${data.id}`, data);
  },
  async cancelTask(taskId: number): Promise<AxiosResponse<boolean>> {
    return await API.put(`/${endpoint}/tasks/cancel/${taskId}`);
  },
  async publishTask(taskIds: number[]): Promise<AxiosResponse<boolean>> {
    return await API.put(`/${endpoint}/tasks/publish`, { taskIds });
  },
  async unpublishTask(taskIds: number[]): Promise<AxiosResponse<boolean>> {
    return await API.put(`/${endpoint}/tasks/unpublish`, { taskIds });
  },
  async createCrew(data: DespatchCrewCreate): Promise<AxiosResponse<Team>> {
    return await API.post(`/${endpoint}/tasks/crew`, data);
  },
  async persistCrewData(data: DespatchCrewPersist): Promise<AxiosResponse<boolean>> {
    return await API.put(`/${endpoint}/tasks/team`, data);
  },
  async persistCrewsData(data: DespatchCrewPersist[]): Promise<AxiosResponse<boolean>> {
    return await API.put(`/${endpoint}/tasks/teams`, data);
  },
  async removeCrew(
    payload: Pick<DespatchCrewRemove, "id" | "companyId" | "isCrew" | "name">,
    createdTo: Date
  ): Promise<AxiosResponse<boolean>> {
    return await API.put(`/${endpoint}/tasks/crew/delete`, { ...payload, createdTo: createdTo });
  },
  async hideCrew(
    payload: Pick<DespatchCrewHide, "id" | "companyId" | "isCrew" | "name">,
    createdTo: Date
  ): Promise<AxiosResponse<boolean>> {
    return await API.put(`/${endpoint}/tasks/crew/hide`, { ...payload, createdTo: createdTo });
  },
  async createTaskComment(data: DespatchCreateTaskComment): Promise<AxiosResponse<TaskComment>> {
    return await API.post(`/${endpoint}/tasks/comments`, data);
  },
  async deleteTaskComment(data: DespatchDeleteTaskComment): Promise<AxiosResponse<boolean>> {
    return await API.delete(`/${endpoint}/tasks/comments/${data.id}`);
  },
  async createPlaceholderTask(data: PlaceholderTask): Promise<AxiosResponse<Task>> {
    return await API.post(`/${endpoint}/tasks/placeholder`, data);
  },
  async deletePlaceholderTask(taskId: number): Promise<AxiosResponse<PlaceholderTask>> {
    return await API.delete(`/${endpoint}/tasks/placeholder/${taskId}`);
  },
};

export default DespatchService;
